/**
 * AdminDashboard — owner-only metrics page.
 * Auto-redirects non-admins.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Users, Activity, TrendingUp, Bell, Share2, Zap, ArrowRight, Crown, AlertCircle, DollarSign, Filter
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color = "#FF4500", testid }) => (
  <div className="bg-[#0A0A0A] border border-white/[0.06] p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <Icon className="w-5 h-5" style={{ color }} />
      <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/30">
        {label}
      </p>
    </div>
    <p className="text-4xl font-heading text-white">{value}</p>
    {sub && <p className="text-[11px] font-mono text-white/40 mt-1">{sub}</p>}
  </div>
);

const Bar = ({ value, max, color = "#FF4500" }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="h-1.5 bg-white/[0.06] w-full">
      <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [signups, setSignups] = useState(null);
  const [features, setFeatures] = useState(null);
  const [retention, setRetention] = useState(null);
  const [users, setUsers] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [o, s, f, r, u] = await Promise.all([
        axios.get(`${API}/admin/overview`, { headers }),
        axios.get(`${API}/admin/signups-by-day?days=30`, { headers }),
        axios.get(`${API}/admin/feature-usage`, { headers }),
        axios.get(`${API}/admin/retention`, { headers }),
        axios.get(`${API}/admin/users?limit=50`, { headers }),
      ]);
      setOverview(o.data);
      setSignups(s.data);
      setFeatures(f.data);
      setRetention(r.data);
      setUsers(u.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
      } else {
        console.error("Admin fetch failed", err);
      }
    }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-[#FF4500] mx-auto mb-4" />
          <h1 className="text-3xl font-heading text-white uppercase mb-2">Access Denied</h1>
          <p className="text-white/40 text-sm mb-6">
            This page is for the Pivot Performance owner only.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-[#FF4500] hover:bg-[#FF6326] text-white px-6 py-2 font-heading uppercase tracking-wider text-xs"
            data-testid="back-to-dash-btn"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-white/40 font-mono text-xs uppercase tracking-widest">Loading admin data...</p>
      </div>
    );
  }

  const maxSignups = Math.max(1, ...(signups?.series || []).map((d) => d.count));

  return (
    <div
      className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative overflow-x-hidden w-full max-w-full"
      data-testid="admin-dashboard"
    >
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto space-y-8 min-w-0"
      >
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-4 h-4 text-[#FF4500]" />
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500]">
                Owner Console
              </p>
            </div>
            <h1 className="text-5xl md:text-6xl font-heading text-white uppercase tracking-tight leading-none">
              Admin
            </h1>
            <p className="text-[10px] font-mono text-white/30 uppercase tracking-[0.2em] mt-2">
              Real numbers · Updated {new Date(overview.generated_at).toLocaleTimeString()}
            </p>
          </div>
          <button
            onClick={fetchAll}
            className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 hover:text-[#FF4500] transition-colors"
            data-testid="refresh-btn"
          >
            Refresh ↻
          </button>
        </div>

        {/* Quick links */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate("/admin/manga")}
            className="bg-[#0A0A0A] hover:bg-[#FF4500] border border-white/[0.06] hover:border-[#FF4500] text-white text-[10px] font-mono uppercase tracking-[0.3em] px-4 py-2 flex items-center gap-2 transition-colors"
            data-testid="open-manga-studio-btn"
          >
            <Crown className="w-3 h-3" /> Manga Studio →
          </button>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] min-w-0">
          <StatCard
            icon={Users}
            label="Total Users"
            value={overview.users.total}
            sub={`+${overview.users.new_this_week} this week`}
            testid="stat-total-users"
          />
          <StatCard
            icon={Activity}
            label="DAU"
            value={overview.active.dau}
            sub={`WAU ${overview.active.wau} · MAU ${overview.active.mau}`}
            color="#CCFF00"
            testid="stat-dau"
          />
          <StatCard
            icon={DollarSign}
            label="MRR"
            value={`$${(overview.mrr || 0).toFixed(2)}`}
            sub={`ARR $${(overview.arr || 0).toFixed(0)} · ${overview.subscriptions.total} subs`}
            color="#10b981"
            testid="stat-mrr"
          />
          <StatCard
            icon={Bell}
            label="Push Enabled"
            value={`${overview.push_opt_in_rate}%`}
            sub={`${overview.push_enabled_users} of ${overview.users.total}`}
            color="#3b82f6"
            testid="stat-push"
          />
        </div>

        {/* Conversion Funnel */}
        {overview.funnel && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] p-6" data-testid="funnel-card">
            <div className="flex items-center justify-between mb-5">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30">
                Conversion Funnel
              </p>
              <Filter className="w-4 h-4 text-[#FF4500]" />
            </div>
            <div className="space-y-4">
              {[
                ["Signed Up", overview.funnel.signups, 100, "#FF4500"],
                ["Email Verified", overview.funnel.verified, overview.funnel.verify_pct, "#FFD700"],
                ["Onboarded", overview.funnel.onboarded, overview.funnel.onboard_pct, "#3b82f6"],
                ["Paid Subscriber", overview.funnel.paid, overview.funnel.paid_pct, "#10b981"],
              ].map(([label, value, pct, color]) => (
                <div key={label} data-testid={`funnel-row-${String(label).toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-white">{label}</span>
                    <span className="text-xs font-mono text-white/60">
                      <span className="text-white font-bold">{value}</span>
                      <span className="text-white/30 ml-2">{pct}%</span>
                    </span>
                  </div>
                  <Bar value={pct} max={100} color={color} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subscription breakdown */}
        <div className="grid grid-cols-3 gap-px bg-white/[0.06]">
          <StatCard
            icon={Crown}
            label="Basic · $9.99"
            value={overview.subscriptions.basic}
            color="#9CA3AF"
            testid="stat-sub-basic"
          />
          <StatCard
            icon={Crown}
            label="Pro · $14.99"
            value={overview.subscriptions.pro}
            color="#FFD700"
            testid="stat-sub-pro"
          />
          <StatCard
            icon={Crown}
            label="Elite · $24.99"
            value={overview.subscriptions.elite}
            color="#FF4500"
            testid="stat-sub-elite"
          />
        </div>

        {/* Retention curve */}
        {retention && retention.cohort_size > 0 && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] p-6" data-testid="retention-card">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30">
                Retention (30-Day Cohort · {retention.cohort_size} signups)
              </p>
              <TrendingUp className="w-4 h-4 text-[#FF4500]" />
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/[0.06]">
              {["d1", "d7", "d30"].map((k) => {
                const v = retention[`${k}_pct`];
                const eligible = retention[`${k}_eligible`];
                return (
                  <div key={k} className="bg-[#0A0A0A] p-4">
                    <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30 mb-2">
                      {k.toUpperCase()}
                    </p>
                    <p className="text-3xl font-heading text-white">
                      {v == null ? "—" : `${v}%`}
                    </p>
                    <p className="text-[10px] font-mono text-white/30 mt-1">
                      {eligible || 0} eligible
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Signups by day */}
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-6" data-testid="signups-chart">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30 mb-4">
            New Signups · Last 30 Days
          </p>
          <div className="flex items-end gap-1 h-32">
            {signups?.series?.map((d) => {
              const h = maxSignups > 0 ? (d.count / maxSignups) * 100 : 0;
              return (
                <div
                  key={d.date}
                  className="flex-1 bg-white/[0.06] hover:bg-[#FF4500] transition-colors group relative"
                  style={{ minHeight: "2px", height: `${Math.max(h, d.count > 0 ? 6 : 2)}%` }}
                  title={`${d.date}: ${d.count}`}
                >
                  {d.count > 0 && (
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[9px] font-mono text-white/0 group-hover:text-white opacity-0 group-hover:opacity-100">
                      {d.count}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] font-mono text-white/30 uppercase mt-2">
            <span>30d ago</span>
            <span>today</span>
          </div>
        </div>

        {/* Feature usage */}
        {features && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] p-6" data-testid="feature-usage">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30 mb-4">
              Feature Usage · Last 30 Days
            </p>
            <div className="space-y-3">
              {[
                ["Workouts logged", features.workouts, Activity],
                ["Vertical jumps", features.vertical_jumps_logged, Zap],
                ["Recovery entries", features.recovery_entries, Activity],
                ["AI drill analyses", features.ai_drill_analyses, Zap],
                ["AI coach chats", features.ai_coach_messages, Zap],
                ["Team challenges", features.team_challenges_created, Users],
              ].map(([label, value, Icon]) => {
                const max = Math.max(
                  features.workouts,
                  features.vertical_jumps_logged,
                  features.recovery_entries,
                  1
                );
                return (
                  <div key={label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5 text-[#FF4500]" />
                        <span className="text-xs text-white">{label}</span>
                      </div>
                      <span className="text-xs font-mono text-white font-bold">{value}</span>
                    </div>
                    <Bar value={value} max={max} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Today's Quote Engagement */}
        <div className="grid sm:grid-cols-2 gap-px bg-white/[0.06]">
          <StatCard
            icon={Share2}
            label="Quote Views Today"
            value={overview.quotes.views_today}
            color="#3b82f6"
            testid="stat-quote-views"
          />
          <StatCard
            icon={Share2}
            label="Quote Shares Today"
            value={overview.quotes.shares_today}
            color="#CCFF00"
            testid="stat-quote-shares"
          />
        </div>

        {/* Users table */}
        {users && (
          <div className="bg-[#0A0A0A] border border-white/[0.06] p-6" data-testid="users-table">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30">
                Recent Users · Newest First
              </p>
              <span className="text-[10px] font-mono text-white/30">{users.count} shown</span>
            </div>
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-xs min-w-[640px]">
                <thead>
                  <tr className="border-b border-white/[0.06] text-white/30 font-mono uppercase tracking-[0.15em] text-[9px]">
                    <th className="text-left py-2 pr-4">Email</th>
                    <th className="text-left py-2 pr-4">Joined</th>
                    <th className="text-right py-2 pr-4">Workouts</th>
                    <th className="text-right py-2 pr-4">Streak</th>
                    <th className="text-left py-2">Last Workout</th>
                  </tr>
                </thead>
                <tbody>
                  {users.users.map((u) => (
                    <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="py-2 pr-4 text-white font-mono">{u.email}</td>
                      <td className="py-2 pr-4 text-white/50">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-4 text-right text-white">{u.workout_count}</td>
                      <td className="py-2 pr-4 text-right text-[#FF4500] font-bold">
                        {u.streak > 0 ? `🔥 ${u.streak}` : "—"}
                      </td>
                      <td className="py-2 text-white/40">
                        {u.last_workout_at
                          ? new Date(u.last_workout_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
